/** Display-only date formatting driven by the `timeZone` returned from /homepage (PT => MM/DD/YYYY, otherwise DD-MM-YYYY). */
export function isUsDateFormat(timeZone?: string | null): boolean {
  return (timeZone ?? '').trim().toUpperCase() === 'PT';
}

/** Formats day/month/year parts using the separator and order for the given timeZone. */
export function formatDateParts(day: string, month: string, year: string, timeZone?: string | null): string {
  return isUsDateFormat(timeZone) ? `${month}/${day}/${year}` : `${day}-${month}-${year}`;
}

/** Splits a raw "dd-MM-yyyy[, HH:mm]" (or "MM/dd/yyyy[, HH:mm]") value into its parts; returns null if unparseable. */
export function splitRawDateTime(value: string | null | undefined): { day: string; month: string; year: string; time: string } | null {
  const [datePart = '', timePart = ''] = (value ?? '').split(',').map((part) => part.trim());
  const isSlash = datePart.includes('/');
  const tokens = datePart.split(isSlash ? '/' : '-');
  const [first, second, year] = tokens;

  if (tokens.length !== 3 || !first || !second || !year) {
    return null;
  }

  return isSlash
    ? { day: second, month: first, year, time: timePart }
    : { day: first, month: second, year, time: timePart };
}

/** Formats a raw "dd-MM-yyyy, HH:mm" value for display; unparseable values are returned unchanged. */
export function formatDateTimeForDisplay(value: string | null | undefined, timeZone?: string | null): string {
  const parts = splitRawDateTime(value);
  if (!parts) {
    return value ?? '';
  }

  const date = formatDateParts(parts.day, parts.month, parts.year, timeZone);
  return parts.time ? `${date}, ${parts.time}` : date;
}

/** Formats a yyyy-MM-dd input value for display; empty/invalid values return an empty string. */
export function formatIsoDateForDisplay(value: string | null | undefined, timeZone?: string | null): string {
  const [year, month, day] = (value ?? '').split('-');
  return year && month && day ? formatDateParts(day, month, year, timeZone) : '';
}
